import Button from '@ui/Button';
import Input from '@ui/Input';
import React, { useEffect } from 'react';
import * as yup from 'yup';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@ui/Typography';
import Select from '@ui/Select';
import { getAllISOCodes } from 'iso-country-currency';
import { defaults, pick, remove, sortBy } from 'lodash-es';
import Checkbox from '@/components/next-ui/Checkbox';

const schema = yup.object().shape({
  country: yup
    .object()
    .required('Please select a country')
    .typeError('Please select a country'),
  zipcode: yup
    .string()
    .required('Zipcode is required')
    .min(5, 'Zipcode must be at least 5 characters')
    .max(10, 'Zipcode must be at most 10 characters'),
  linkedInProfile: yup
    .string()
    .url('Linkedin profile must be a url')
    .max(100, 'LinkedIn URL must be less than 100 characters')
    .notRequired()
    .nullable(),
  state: yup
    .string()
    // .min(2, 'State must be at least 2 characters')
    .max(20, 'State must be at most 20 characters'),
  // .notRequired()
  // .nullable(),
  city: yup
    .string()
    // .min(2, 'City must be at least 2 characters')
    .max(20, 'City must be at most 20 characters'),
  // .notRequired()
  // .nullable(),
  agreeToTerms: yup.boolean().isTrue('You must agree to the terms of service'),
});

const getCountries = () => {
  const countries = sortBy(
    getAllISOCodes().map((country) => ({
      label: country.countryName,
      id: country.iso,
      currency: country.currency,
      currencySymbol: country.symbol,
    })),
    'label',
  );

  const priorityCountries = remove(countries, (country) =>
    ['US', 'IN', 'MX'].includes(country.id),
  );

  return [
    priorityCountries.find((c) => c.id === 'US'),
    priorityCountries.find((c) => c.id === 'IN'),
    priorityCountries.find((c) => c.id === 'MX'),
    ...countries,
  ];
};

function JoinFormLocationStep(props) {
  const { onSubmit, onBack, formData, handleFormData } = props;

  const defaultValues = defaults(
    pick(formData, [
      'country',
      'zipcode',
      'linkedInProfile',
      'state',
      'city',
      'agreeToTerms',
    ]),
    {
      linkedInProfile: '',
      zipcode: '',
      country: null,
      state: '',
      city: '',
      agreeToTerms: false,
    },
  );

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
  } = form;

  const countries = getCountries();

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  const handleFormBack = () => {
    onBack(getValues());
  };

  const formValues = useWatch({ control });

  useEffect(() => {
    handleFormData((prevFormData) => {
      const currentFormData = {
        ...prevFormData,
        country: formValues?.country?.id,
        state: formValues.state,
        city: formValues.city,
      };
      return currentFormData;
    });
  }, [formValues]);

  return (
    <div className="flex h-full w-full flex-1 flex-col gap-4">
      <Typography variant="h3">Where are you located?</Typography>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex h-full w-full flex-col">
        <div className="flex w-full flex-1 flex-col gap-4 overflow-y-auto">
          <div className="flex gap-4">
            <div className="flex-1">
              <Controller
                name="country"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Select
                    label="COUNTRY"
                    placeholder="Country"
                    required
                    options={countries}
                    {...rest}
                    error={errors.country?.message}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <Controller
                name="state"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="STATE"
                    placeholder="State"
                    // required
                    {...rest}
                    error={errors.state?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Controller
                name="city"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="CITY"
                    placeholder="City"
                    // required
                    {...rest}
                    error={errors.city?.message}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <Controller
                name="zipcode"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="ZIPCODE"
                    placeholder="Zipcode"
                    required
                    {...rest}
                    error={errors.zipcode?.message}
                  />
                )}
              />
            </div>
          </div>

          <Controller
            name="linkedInProfile"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Input
                label="LINKEDIN"
                placeholder="Linkedin"
                {...rest}
                error={errors.linkedInProfile?.message}
              />
            )}
          />

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Controller
                name="agreeToTerms"
                control={control}
                render={({ field: { onChange } }) => (
                  <Checkbox
                    onChange={onChange}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography variant="body2" component="div">
                I have read and agree to the
                <Typography
                  target="_blank"
                  component="a"
                  variant="body2"
                  href="https://www.neki.io/terms-and-conditions"
                  className="mx-0.5 underline hover:text-text-hyperlink">
                  terms of service
                </Typography>
              </Typography>
            </div>
            {errors.agreeToTerms?.message && (
              <Typography variant="caption" color="error">
                {errors.agreeToTerms?.message}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex w-full justify-between pt-3">
          <Button variant="outlined" color="primary" onClick={handleFormBack}>
            Back
          </Button>

          <Button variant="filled" color="primary" type="submit">
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
}

export default JoinFormLocationStep;
